import type * as React from "react";

import { Dot } from "@beweegbaas/ui/components/ui/dot";
import { cn } from "@beweegbaas/ui/lib/utils";

/**
 * The time of now as a line across a block of time, such as a lesuur, at `position`: how far now
 * is into it, from 0 to 1. The block is its `relative` parent; the line stays clear of its edges.
 * Decoration: what runs now says so in words elsewhere.
 */
function TimeIndicator({
  position,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & { position: number }) {
  return (
    <div
      aria-hidden
      data-slot="time-indicator"
      className={cn("pointer-events-none absolute inset-x-0 z-10 h-0", className)}
      style={{ top: `clamp(10px, ${position * 100}%, calc(100% - 10px))`, ...style }}
      {...props}
    >
      <span className="bg-primary absolute inset-x-0 -top-px h-0.5 rounded-full" />
      {children}
    </div>
  );
}

/** The time on the line's left end. */
function TimeIndicatorLabel({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="time-indicator-label"
      className={cn(
        "bg-primary text-primary-foreground ring-background absolute top-0 left-0 flex h-5 -translate-y-1/2 items-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ring-2",
        className,
      )}
      {...props}
    />
  );
}

/** A dot on the line's right end, where it meets what it points at. */
function TimeIndicatorDot({ className, ...props }: React.ComponentProps<typeof Dot>) {
  return (
    <Dot
      data-slot="time-indicator-dot"
      className={cn("bg-primary absolute -top-1 -right-1", className)}
      {...props}
    />
  );
}

export { TimeIndicator, TimeIndicatorDot, TimeIndicatorLabel };
